import React, { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { toast } from "@/lib/toast"
import { apiClient } from "@/api/client"
import { useQueryClient } from "@tanstack/react-query"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { productSchema, type ProductFormData } from "@/lib/validationSchemas"
import { type ApiError } from "@/api/types"
import { ProductFields } from "./ProductFields"
import { Form } from "@/components/ui/form"
import { useLanguage } from "@/hooks/useLanguage"
import { toastSuccess, toastError } from "@/lib/toast"

const AddProductForm = () => {
  const queryClient = useQueryClient()
  const [loading, setLoading] = useState(false)
  const { t, language } = useLanguage()
  const text = t.productForm

  const form = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      weightUnit: "kg",
      lowStockThreshold: 5,
      primaryImageIndex: 0,
      consumptionType: "Human",
      imageFiles: [],
      technicalSpecFile: [],
    },
  })

  const { handleSubmit, reset, setError } = form

  const onSubmit = async (data: ProductFormData) => {
    setLoading(true)
    try {
      //create

      await apiClient.createProduct(
        data.name,
        data.sku || undefined,
        data.description,
        data.price,
        data.unitCost,
        data.weight,
        data.weightUnit,
        data.initialStock,
        data.lowStockThreshold,
        data.leadTimeDays,
        data.safetyStockLevel,
        data.isTaxable,
        data.categoryId,
        data.providerId || undefined,
        data.warehouseId || undefined,
        data.primaryImageIndex,
        data.consumptionType as any,
        data.imageFiles.length > 0 ? data.imageFiles : undefined,
        data.technicalSpecFile && data.technicalSpecFile.length > 0
          ? data.technicalSpecFile[0]
          : undefined
      )

      toastSuccess(text.success)
      reset()
      queryClient.invalidateQueries({ queryKey: ["vendor-products"] })
    } catch (err: unknown) {
      const error = err as ApiError
      console.error("Error saving product:", error)

      // Handle API validation errors
      if (error.response) {
        try {
          const errorData = JSON.parse(error.response)
          if (errorData.errors) {
            Object.keys(errorData.errors).forEach((key) => {
              const field = (key.charAt(0).toLowerCase() + key.slice(1)) as keyof ProductFormData
              setError(field, {
                type: "manual",
                message: errorData.errors[key][0],
              })
            })
            toastError(text.validationError)
            return
          }
        } catch (e) {
          console.error("Failed to parse error response", e)
        }
      }
      toastError("Error: " + (error.message || "Failed to create product"))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="mx-auto w-full max-w-[1000px] space-y-12 pb-20">
      {/* Page Title Section */}
      <div className="mb-16 flex flex-col items-center space-y-4 pt-12 text-center">
        <h1 className="font-display text-6xl leading-none tracking-display text-foreground md:text-7xl">
          {text.title}
        </h1>
        <div className="h-1.5 w-12 bg-primary" />
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-background shadow-2xl transition-all duration-500">
        <div className="p-8 md:p-16">
          <Form {...form}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-12">
              <ProductFields
                register={form.register}
                errors={form.formState.errors}
                setValue={form.setValue}
                watch={form.watch}
                control={form.control}
              />

              <Button
                type="submit"
                className="mt-12 h-14 w-full rounded-lg bg-action font-display text-lg tracking-widest text-primary-foreground shadow-lg transition-all duration-300 hover:opacity-90 active:scale-[0.98]"
                disabled={loading}
              >
                {loading ? "..." : text.submit}
              </Button>
            </form>
          </Form>
        </div>
      </div>
    </div>
  )
}

export default AddProductForm

